import { getLocalStorageContext } from 'utils/localStorage/localStorageHelper';

import { LpChatMessage, LpChatMessageSource } from './types';

const lpChatEventsLocalStorage = getLocalStorageContext('LP_CHAT_EVENTS');
const LAST_MESSAGE_TIMESTAMP_KEY = 'LAST_MESSAGE_TIMESTAMP';

interface EventListeners {
  botMessageEventListener: (by: string) => void;
  botOptionsPresentedEventListener: (by: string, title: string, position: number) => void;
  botOptionSelectedEventListener: (by: string, title: string, position: number) => void;
  advisorMessageEventListener: (by: string) => void;
  customerMessageEventListener: () => void;
}

const chatEventContext = {
  messageIdAlreadySeen: new Set<number>(),
  lastBotOptionsBy: '' as string,
  lastBotOptions: [] as string[],
  eventListeners: undefined as EventListeners | undefined,
  chatMessageQueue: [] as LpChatMessage[],
  defaultLastMessageTime: '0',
};

export const resetChatEventContext = () => {
  chatEventContext.messageIdAlreadySeen = new Set();
  chatEventContext.chatMessageQueue = [];
  chatEventContext.lastBotOptionsBy = '';
  chatEventContext.lastBotOptions = [];
  chatEventContext.eventListeners = undefined;
};

const isBotMetadataPresent = ({ metadata }: LpChatMessage) =>
  Boolean(metadata?.find(({ type }) => type === 'CbBotResponseMetaData'));

const byAdvisorMessage = (by?: string) => /\w+ \w/.test(by?.trim() ?? '');

const byBot = (by?: string) => by?.toLowerCase() === 'bot';

const isBotMessage = (message: LpChatMessage) =>
  isBotMetadataPresent(message) || !byAdvisorMessage(message.by) || byBot(message.by);

const handleBotMessage = ({ quickReplies, type, text, by }: LpChatMessage) => {
  chatEventContext.eventListeners?.botMessageEventListener(by ?? '');
  quickReplies?.replies
    .filter(quickReply => quickReply.type === 'button')
    .forEach((quickReply, position) =>
      chatEventContext.eventListeners?.botOptionsPresentedEventListener(by ?? '', quickReply.title, position),
    );
  if (type === 'richContent' && typeof text !== 'string') {
    text.elements
      ?.filter(element => element.type === 'button')
      .forEach((element, position) =>
        chatEventContext.eventListeners?.botOptionsPresentedEventListener(by ?? '', element.title, position),
      );
  }
};

const handleAdvisorMessage = (by: string) => {
  chatEventContext.eventListeners?.advisorMessageEventListener(by);
};

const handleBotOptionSelected = (text: string) => {
  const position = chatEventContext.lastBotOptions.indexOf(text);
  chatEventContext.eventListeners?.botOptionSelectedEventListener(chatEventContext.lastBotOptionsBy, text, position);
};

const handleCustomerMessage = () => {
  chatEventContext.eventListeners?.customerMessageEventListener();
};

const chatEventHandler = (message: LpChatMessage) => {
  const { history, ['@id']: id, quickReplies, source, text, time, isWelcomeMessage, by } = message;

  // 1. LP also populates historic messages (messages from previous chat) - ignore these.
  // 2. We don't care about welcoming messages
  if (history || isWelcomeMessage) {
    return;
  }

  // LP normally pushes the same message multiple times - first push already has everything we need
  if (chatEventContext.messageIdAlreadySeen.has(id)) {
    return;
  }
  chatEventContext.messageIdAlreadySeen.add(id);

  // Message has already been seen. Likely because customer did a page navigation.
  const lastChatMessageTimestamp = parseFloat(
    lpChatEventsLocalStorage.getValue(LAST_MESSAGE_TIMESTAMP_KEY) ?? chatEventContext.defaultLastMessageTime,
  );
  if (lastChatMessageTimestamp >= time) {
    return;
  }
  lpChatEventsLocalStorage.setValue(LAST_MESSAGE_TIMESTAMP_KEY, time.toString());

  if (source === LpChatMessageSource.agent) {
    if (quickReplies?.replies.length) {
      chatEventContext.lastBotOptions = quickReplies.replies.map(qr => qr.title);
      chatEventContext.lastBotOptionsBy = by ?? '';
    }
    if (typeof text !== 'string' && text?.elements?.length) {
      chatEventContext.lastBotOptions = text?.elements.filter(el => el.type === 'button').map(el => el.title);
      chatEventContext.lastBotOptionsBy = by ?? '';
    }
    if (isBotMessage(message)) {
      handleBotMessage(message);
    } else {
      handleAdvisorMessage(by ?? '');
    }
  } else if (source === LpChatMessageSource.visitor) {
    if (typeof text === 'string' && chatEventContext.lastBotOptions.includes(text)) {
      handleBotOptionSelected(text);
    } else {
      handleCustomerMessage();
    }
  }
};

const dispatchChatEvents = () => {
  if (chatEventContext.eventListeners) {
    chatEventContext.chatMessageQueue.forEach(message => chatEventHandler(message));
  }
};

export const registerLpChatEventListeners = (listeners: EventListeners) => {
  // When the local storage is disabled, we want to store the current timestamp to not register prev messages of the chat.
  chatEventContext.defaultLastMessageTime = lpChatEventsLocalStorage.isAvailable ? '0' : Date.now().toString();
  chatEventContext.eventListeners = listeners;
  dispatchChatEvents();
};

export const chatMessageListener = (messages: LpChatMessage[]) => {
  chatEventContext.chatMessageQueue = [...chatEventContext.chatMessageQueue, ...messages].sort(
    (a, b) => a.time - b.time,
  );
  dispatchChatEvents();
};
